/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Slash Command Definitions and Actions (Task 10.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type { Editor, Range } from '@tiptap/core';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code2,
  Table as TableIcon,
  Minus,
  ImageIcon,
  Sparkles,
  Bot,
  type LucideIcon,
} from 'lucide-react';

export interface SlashCommandItem {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  category: 'basic' | 'ai' | 'media';
  command: (props: { editor: Editor; range: Range }) => void;
}

export const SLASH_COMMANDS: SlashCommandItem[] = [
  // AI Commands
  {
    id: 'ai-write-prompt',
    title: 'AI Prompt / Ask AI',
    description: 'Request AI assistant to draft or generate content',
    icon: Sparkles,
    category: 'ai',
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: '✨ [AI Prompt: Enter your drafting request here...]',
            },
          ],
        })
        .run();
    },
  },
  {
    id: 'ai-summary',
    title: 'AI Summary Box',
    description: 'Insert a smart summary callout block',
    icon: Bot,
    category: 'ai',
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setBlockquote()
        .insertContent('📌 Core Summary: ')
        .run();
    },
  },

  // Basic Blocks
  {
    id: 'heading-1',
    title: 'Heading 1',
    description: 'Large section heading (H1)',
    icon: Heading1,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run();
    },
  },
  {
    id: 'heading-2',
    title: 'Heading 2',
    description: 'Medium section heading (H2)',
    icon: Heading2,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run();
    },
  },
  {
    id: 'heading-3',
    title: 'Heading 3',
    description: 'Small subsection heading (H3)',
    icon: Heading3,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run();
    },
  },
  {
    id: 'bullet-list',
    title: 'Bullet List',
    description: 'Unordered bulleted list',
    icon: List,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    id: 'numbered-list',
    title: 'Numbered List',
    description: 'Numbered sequential list',
    icon: ListOrdered,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    id: 'task-list',
    title: 'Task Checklist',
    description: 'Interactive checklist with checkboxes',
    icon: CheckSquare,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    id: 'blockquote',
    title: 'Quote',
    description: 'Highlighted blockquote block',
    icon: Quote,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    id: 'code-block',
    title: 'Code Block',
    description: 'Syntax-highlighted code container',
    icon: Code2,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    id: 'table',
    title: 'Table (3x3)',
    description: 'Insert 3x3 editable data table',
    icon: TableIcon,
    category: 'basic',
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
  {
    id: 'horizontal-rule',
    title: 'Divider',
    description: 'Visual horizontal rule divider',
    icon: Minus,
    category: 'basic',
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },

  // Media
  {
    id: 'image',
    title: 'Image',
    description: 'Insert image via URL or upload',
    icon: ImageIcon,
    category: 'media',
    command: ({ editor, range }) => {
      const url = window.prompt('Enter image URL:');
      if (url) {
        editor.chain().focus().deleteRange(range).setImage({ src: url }).run();
      }
    },
  },
];

export function filterSlashCommands(query: string): SlashCommandItem[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return SLASH_COMMANDS;

  return SLASH_COMMANDS.filter(
    (item) =>
      item.title.toLowerCase().includes(normalized) ||
      item.description.toLowerCase().includes(normalized) ||
      item.id.toLowerCase().includes(normalized),
  );
}
